import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { api } from '../api/client'
import { keys } from '../api/hooks'
import { pendingSends } from './sends'
import type { ServerEvent } from '../api/types'
import { useUi } from '../store/ui'

/** Server-sent events: the worker's IDLE/sync pushes flow here via Redis pub/sub. EventSource auto-reconnects. */
export function useRealtime(enabled: boolean) {
  const qc = useQueryClient()
  const notify = useUi((s) => s.notify)
  useEffect(() => {
    if (!enabled) return
    const es = new EventSource('/api/events', { withCredentials: true })
    es.onmessage = (e) => {
      let ev: ServerEvent
      try { ev = JSON.parse(e.data) as ServerEvent } catch { return }
      switch (ev.type) {
        case 'mail.new':
          void qc.invalidateQueries({ queryKey: ['messages'] }); void qc.invalidateQueries({ queryKey: keys.folders }); void qc.invalidateQueries({ queryKey: keys.labels })
          if (ev.folder.toUpperCase() === 'INBOX') notify({ message: ev.count === 1 ? 'New message' : `${ev.count} new messages`, severity: 'info' })
          break
        case 'mail.changed': case 'mail.expunged':
          void qc.invalidateQueries({ queryKey: ['messages'] }); void qc.invalidateQueries({ queryKey: keys.folders }); void qc.invalidateQueries({ queryKey: keys.labels })
          break
        case 'folders.changed': void qc.invalidateQueries({ queryKey: keys.folders }); break
        case 'labels.changed': void qc.invalidateQueries({ queryKey: keys.labels }); break
        case 'outbox.status': {
          void qc.invalidateQueries({ queryKey: keys.outbox })
          if (!pendingSends.has(ev.id)) break
          if (ev.status === 'SENT') { pendingSends.delete(ev.id); void qc.invalidateQueries({ queryKey: ['messages'] }); void qc.invalidateQueries({ queryKey: keys.folders }) }
          if (ev.status === 'FAILED') notify({ message: `Message not sent${ev.error ? `: ${ev.error}` : ''}`, severity: 'error', actionLabel: 'Retry', onAction: () => void api(`/outbox/${ev.id}/retry`, { method: 'POST' }), durationMs: 12000 })
          break
        }
      }
    }
    return () => es.close()
  }, [enabled, qc, notify])
}
